import { useState } from "react";
import userManagementService from "./userManagementService";
import { Space, Tooltip, Tag, message, Popconfirm, Button } from "antd";
import { useQuery } from "react-query";
import useDebounce from "../../../core/hooks/useDebounce.js";

const useUserManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState({});
  const { debouncedValue } = useDebounce(searchQuery, 1000);
  const [openEditModalUser, setOpenEditModalUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const getUsersOptions = {
    ...options,
    search: debouncedValue,
  };

  const {
    data: users,
    isLoading,
    isFetching,
    refetch: refetchUsers,
    error,
  } = useQuery(
    ["usersData", debouncedValue, options],
    () => userManagementService.getUsersList(getUsersOptions),
    {
      initialData: {
        data: { data: [], meta: { hasPrevious: false, hasNext: false } },
      },
      keepPreviousData: true,
    }
  );

  if (users) {
    users.data.data = users.data.data.map((user, idx) => {
      return {
        ...user,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        userTitle: user.title,
        department: "Development",
        index:
          users.data.meta.page === 1
            ? idx + 1
            : (users.data.meta.page - 1) * users.data.meta.take + (idx + 1),
        key: idx,
      };
    });
  }

  const handleEditUser = (record, e) => {
    e.preventDefault();
    setSelectedUser(record);
    setOpenEditModalUser(true);
  };

  const onChange = (pagination, filters, sorter, extra) => {
    setOptions({
      ...options,
      page: pagination.current,
      take: pagination.pageSize,
    });
  };

  const handleSearchChange = (event) => {
    if (event.target.value.length >= 2) {
      setSearchQuery(event.target.value);
    } else {
      setSearchQuery("");
    }
  };

  const handleChangeStatusUser = async (id, e) => {
    e.stopPropagation();
    try {
      await userManagementService.changeUserStatus(id);
      await refetchUsers();
      message.success("Status changed successfully");
    } catch (error) {
      console.log(error);
      message.warning(
        "An error occurred while changing the status. Please try again later."
      );
    }
  };

  const cancel = (e) => {
    e.stopPropagation();
  };

  const rowClassName = (record) => {
    return record.isDeleted ? "ant-table-row-deleted" : "";
  };

  const columns = [
    {
      title: "  ",
      dataIndex: "index",
      key: "index",
    },
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
    },
    {
      title: "User Title",
      dataIndex: "userTitle",
      key: "userTitle",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      render: (roles) => (
        <>
          {roles.map((role) => (
            <Tag color="blue" key={role}>
              {role}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          <Tooltip title="Edit User">
            <Button
            type="primary"
              onClick={(e) => handleEditUser(record, e)}
              className="btn-edit-user"
            >
              Edit
              </Button>
          </Tooltip>
          <Popconfirm
            title={
              record.isDeleted
                ? "Do you want to activate this user?"
                : "Do you want to deactivate this user?"
            }
            onConfirm={(e) => handleChangeStatusUser(record.id, e)}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type={"default"}
              danger={!record.isDeleted}
              onClick={(e) => e.stopPropagation()}
            >
              {record.isDeleted ? "Activate" : "Deactivate"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return {
    users: users.data,
    isLoading,
    isFetching,
    error,
    searchQuery,
    setSearchQuery,
    options,
    setOptions,
    columns,
    onChange,
    handleSearchChange,
    rowClassName,
    refetchUsers,
    openEditModalUser,
    setOpenEditModalUser,
    selectedUser,
    handleEditUser,
  };
};
export default useUserManagement;
